import { EllipsisIcon } from '@daily/shared/components/Icons/EllipsisIcon';
import { PinIcon } from '@daily/shared/components/Icons/PinIcon';
import { RefreshIcon } from '@daily/shared/components/Icons/RefreshIcon';
import { Menu } from '@daily/shared/components/Menu';
import { Stack } from '@daily/shared/components/Stack';
import { Tab } from '@daily/shared/components/Tabs';
import { Text } from '@daily/shared/components/Text';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CustomIntegration,
  useCustomIntegrations,
} from '../../hooks/useCustomIntegrations';
import { usePinnedId } from '../../lib/state/layout';
import { AppIcon } from '../Apps/AppIcon';

interface Props {
  index: number;
  integration: CustomIntegration;
}

export const MainIntegrationTab: React.FC<Props> = ({ index, integration }) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [pinnedId, setPinnedId] = usePinnedId();
  const { canControlIntegration, stopIntegration } = useCustomIntegrations();

  const btnId = useId();
  const menuId = useId();

  const handleClickPin = () => {
    setPinnedId((p) => (p === integration.id ? null : integration.id));
    setShowMenu(false);
  };

  const handleClickRefresh = () => {
    const iframe = document.querySelector<HTMLIFrameElement>(
      `iframe[id="iframe-integration-${integration.id}"]`
    );
    iframe.src = iframe.src;
    setShowMenu(false);
  };

  const handleClickClose = () => {
    setShowMenu(false);
    stopIntegration(integration.id);
  };

  useEffect(() => {
    setShowMenu(false);
  }, [pinnedId]);

  return (
    <>
      <Tab
        index={index}
        icon={<AppIcon id={integration.id} size={16} style={{ padding: 1 }} />}
        menuButton={{
          'aria-controls': menuId,
          id: btnId,
          children: (
            <>
              <EllipsisIcon size={16} />
              <VisuallyHidden>
                {t('integrations.showOptions', {
                  label: integration.label,
                })}
              </VisuallyHidden>
            </>
          ),
          onClick: () => setShowMenu(true),
          open: showMenu,
        }}
      >
        <Stack align="center" gap={4} horizontal>
          <Text El="span" style={{ maxWidth: 200 }} truncate>
            {integration.label}
          </Text>
          {pinnedId === integration.id && <PinIcon size={16} />}
        </Stack>
      </Tab>
      {showMenu && (
        <Menu
          id={menuId}
          align="left"
          aria-labelledby={btnId}
          offsetTop={0}
          placement="bottom"
          items={[
            {
              label:
                pinnedId === integration.id
                  ? t('integrations.unpin', { label: integration.label })
                  : t('integrations.pin', { label: integration.label }),
              icon: <PinIcon size={16} />,
              onClick: handleClickPin,
            },
            {
              label: t('integrations.refresh', { label: integration.label }),
              icon: <RefreshIcon size={16} />,
              onClick: handleClickRefresh,
            },
            canControlIntegration(integration)
              ? {
                  label: t('integrations.close', { label: integration.label }),
                  onClick: handleClickClose,
                }
              : null,
          ].filter(Boolean)}
          onClose={() => setShowMenu(false)}
        />
      )}
    </>
  );
};
